import React, { useState } from 'react';
import { UserCircleDisplay } from '../../utils/UserCustomDisplay';
import { StatusBadge, StatusBadgeSm, getStatusClassAndText } from '../../utils/StatusBadge';
import DocumentStatusTable from './DocumentStatusTable';
import TrimContent from '../../utils/TrimContent';
import { useTimezone } from '../../utils/TimezoneContext';
import LinkedProjectsModal from './LinkedProjectsModal';

const UnstructuredThreadDetails = ({ threadDetails, onClose, onRefresh, onFolderDeleted }) => {
  const [showLinkedProjects, setShowLinkedProjects] = useState(false);
  // Add refresh trigger state
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);
  const [showAllTags, setShowAllTags] = useState(false);
  // True while the document panel is open; the folder header makes room for it.
  const [isDocPanelOpen, setIsDocPanelOpen] = useState(false);
  const { formatDateTime } = useTimezone();
  if (!threadDetails) return null;

  const { thread, documents } = threadDetails;
  const linkedProjects = threadDetails.linked_projects || [];
  const MAX_TAGS = 2;
  const tags = thread.tags || [];
  const visibleTags = showAllTags ? tags : tags.slice(0, MAX_TAGS);
  const threadStatus = getStatusClassAndText(thread.upload_status);

  // Combined refresh handler
  const handleRefresh = () => {
    // Call parent's refresh function to update thread details
    onRefresh();
    // Increment refresh trigger to update document table
    setDocumentRefreshTrigger(prev => prev + 1);
  };

  const handleDocumentsUpdated = (documents) => {
    // use this callback if we need to update the parent component
    // or perform any actions when documents are refreshed
    //below function if uncommented will also refresh global thread details component
    //if (onRefresh) {
     // onRefresh(thread.id);
    //}
  };
  
  return (
    <div className="ev-card-4 w-100 ev-padding-xlarge2">
      {!isDocPanelOpen && (
      <>
      <div className="text-end">
        <button className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" onClick={handleRefresh}>
          <i className="bi bi-arrow-repeat"></i> Refresh
        </button>
        <button className="ev-btn-blue-outline-round ms-2" onClick={onClose}>
          <i className="bi bi-x-lg"></i> Close
        </button>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <p className="ev-med-p-blue mb-0 col-4">
           {thread.name}
           &nbsp;
          <button
            type="button"
            className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg ms-2 align-middle avatar-tooltip"
            onClick={() => setShowLinkedProjects(true)}
            data-tooltip="See which projects use this Data Folder"
          >
            <i className="bi bi-link-45deg me-1"></i>
            Linked Projects ({linkedProjects.length})
          </button>
          {/* <span className={`badge badge-sm ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
            {thread.thread_access_level}
          </span> */}
          <br />
          <span className="ev-small-p-dark">Data Folder Owner </span><span className="ev-message-card1 ev-btn-small mt-2 ">{thread.created_by_email}</span>
        </p>

        <div className="ev-card-2 ev-small-p-dark col-4 me-2">
          <p className="mb-1"><strong>Created at </strong> {formatDateTime(thread.created_at)}</p>
          <p className="mb-2"><strong>Updated at </strong> {thread.updated_at ? formatDateTime(thread.updated_at) : 'No Update'}</p>
        </div>

        {thread.thread_access_level === 'PRIVATE'
          && ((thread.user_access || []).length > 0
            || (thread.project_access || []).length > 0
            || thread.created_by_email) && (
          <div className="ev-card-2 col-4">
            <div className="card-body">
              <p className="ev-small-p-dark mb-1">User Access</p>
              <UserCircleDisplay
                users={thread.user_access || []}
                projectUsers={thread.project_access || []}
                ownerEmail={thread.created_by_email}
                maxDisplay={4}
              />
            </div>
          </div>
        )}
      </div>
      
      <div className="row d-flex mb-4">
        <div className="col-4 d-flex">
          <div className="ev-card-2 mb-3 w-100">
            <p className="ev-small-p-dark mb-1">Meta Tags</p>
            {tags.length > 0 && (
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {visibleTags.map((tag, idx) => (
                  <span key={idx} className="ev-message-card3 ev-btn-small">{tag}</span>
                ))}
                {tags.length > MAX_TAGS && (
                  <div
                    className="more-circle more-circle--default d-flex align-items-center justify-content-center ev-btn-blue-outline-round ev-bold-reg avatar-tooltip"
                    onClick={() => setShowAllTags((prev) => !prev)}
                    data-tooltip={showAllTags ? "Show less" : "View all tags"}
                  >
                    {showAllTags ? "−" : `+${tags.length - MAX_TAGS}`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="col-8 d-flex">
          <div className="ev-card-1 mb-3 w-100">
            <div className="card-body">
              <h5 className="ev-reg-p-blue">Overall Status</h5>
              <div className="d-flex justify-content-between align-items-cente">
                <p className="ev-small-p-dark">Migration Status <br /><StatusBadgeSm status={thread.upload_status } /></p>
                <p className="ev-small-p-dark">Standardization <br /><StatusBadgeSm status={thread.standardization_status} /></p> 
                <p className="ev-small-p-dark">Markdown RAG <br /><StatusBadgeSm status={thread.markdown_rag_status} /></p> 
                <p className="ev-small-p-dark">Image RAG <br /><StatusBadgeSm status={thread.image_rag_status} /></p> 
                <p className="ev-small-p-dark">Auto-classification <br /><StatusBadgeSm status={thread.autoclassification_status} /></p>
                <p className="ev-small-p-dark">Parquet <br /><StatusBadgeSm status={thread.parquet_status} /></p> 
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
      <DocumentStatusTable
        threadId={thread.id}
        onDocumentsUpdated={handleDocumentsUpdated}
        externalRefreshTrigger={documentRefreshTrigger}
        onThreadStatusRefresh={handleRefresh}
        onFolderDeleted={onFolderDeleted}
        threadTags={thread.tags || []}
        onPanelOpenChange={setIsDocPanelOpen}
      />

      <LinkedProjectsModal
        show={showLinkedProjects}
        onClose={() => setShowLinkedProjects(false)}
        projects={linkedProjects}
        threadName={thread.name}
      />
    </div>
  );
};

export default UnstructuredThreadDetails;